/**
 * Keeps personal data and one-time secrets out of every third party that
 * records URLs: Google Analytics, Vercel Analytics / Speed Insights and
 * GlitchTip (Sentry).
 *
 * Several pages carry an email address or a one-time secret in the URL —
 * links in our emails cannot change once they are sent, so the pages stay as
 * they are and the URL is cleaned on its way out instead:
 *
 *   /auth-confirm/<email>/<confirmation id>   -> /auth-confirm/[email]/[token]
 *   /auth-account-created/<email>             -> /auth-account-created/[email]
 *   /auth-forgot-sent/<email>                 -> /auth-forgot-sent/[email]
 *   /auth-re-password/<reset token>/<user id> -> /auth-re-password/[token]/[id]
 *   /unsubscribe/<email>/<type>               -> /unsubscribe/[email]/[type]
 *   /magic?id=…&token=…, /coilmind/account?id=…&token=…, /feedback?user=<email>,
 *   /oauth/consent?uid=…, /converter?restore=…  -> the values become [redacted]
 *   /mcp/upload#<token>                       -> the fragment is dropped (always)
 *   /auth-login?from=%2Foauth%2Fconsent%3Fuid%3D…  -> `from` is cleaned recursively
 *
 * plus a catch-all for anything not listed: a path segment or query value that
 * holds an email, a UUID, a long hex string, a JWT or another long random-looking
 * token is masked as well.
 */

/**
 * Sanitise one URL (absolute, or a path with optional query) for analytics.
 *
 * Deliberately self-contained — no references outside its own body — because
 * app/layout.js inlines `String(sanitizeAnalyticsUrl)` into the gtag bootstrap,
 * so that even a hit sent before React hydrates never carries the raw address.
 * Do not move anything out of this function.
 *
 * @param {string} input URL or path
 * @param {string[]} [dropParams] query parameters to remove entirely
 *   (e.g. the Google click ids that break GA4 attribution)
 * @returns {string}
 */
export function sanitizeAnalyticsUrl(input, dropParams) {
  if (typeof input !== 'string' || input === '') return input;

  // [path prefix, placeholders for the segments that follow it]. Matched as
  // whole segments anywhere in the path, so the API endpoints behind these
  // pages (…/users/email/confirm/<id>, …/mcp-uploads/<token>) are covered too.
  const PATH_RULES = [
    ['/auth-confirm', ['[email]', '[token]']],
    ['/auth-account-created', ['[email]']],
    ['/auth-forgot-sent', ['[email]']],
    ['/auth-re-password', ['[token]', '[id]']],
    ['/marketing/unsubscribe', ['[token]']],
    ['/unsubscribe', ['[email]', '[type]']],
    ['/email/confirm', ['[token]']],
    ['/mcp-uploads', ['[token]']],
    ['/oauth/interaction', ['[uid]']],
    ['/add_disposable', ['[email]']],
  ];
  // Query parameters whose value is a secret or personal data, whatever it looks like.
  const SECRET_PARAM = new RegExp([
    '^(token|access_token|refresh_token|id_token|auth|authorization|code|code_verifier',
    '|code_challenge|key|api_key|apikey|secret|client_secret|password|pass|pwd|email|mail',
    '|e|user|username|user_id|userid|uid|id|state|nonce|session|session_id|sid|sig',
    '|signature|hash|otp|ticket|t|jwt|restore|batch|batchkey|interaction|invite|magic',
    '|reset)$',
  ].join(''), 'i');
  // Query parameters that carry another URL (a return path): cleaned recursively.
  const NESTED_PARAM = new RegExp([
    '^(from|next|redirect|redirect_to|redirect_uri|redirecturl|return|return_to|returnto',
    '|returnurl|continue|callback|callbackurl|target|url)$',
  ].join(''), 'i');
  const drop = (dropParams || []).map((name) => String(name).toLowerCase());

  const safeDecode = (value) => {
    try {
      return decodeURIComponent(value.replace(/\+/g, ' '));
    } catch {
      return value;
    }
  };

  // Catch-all for a single path segment or query value.
  const maskValue = (raw) => {
    const v = safeDecode(raw);
    if (v.indexOf('@') !== -1) return '[email]';
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) return '[id]';
    if (/^[0-9a-f]{24,}$/i.test(v)) return '[token]';
    if (/^[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}$/.test(v)) return '[token]';
    // Long random-looking strings: letters and digits mixed, and not a readable
    // slug such as a blog path ("wincc-unified-svg-to-svghmi-widget-converter").
    if (v.length >= 32 && /^[A-Za-z0-9_-]+$/.test(v) && /[0-9]/.test(v) && /[A-Za-z]/.test(v)
      && !/^[a-z0-9]+(-[a-z0-9]+)+$/.test(v)) return '[token]';
    return null;
  };

  const cleanPath = (path) => {
    const segs = path.split('/');
    PATH_RULES.forEach(([prefix, holders]) => {
      const parts = prefix.split('/').slice(1);
      for (let i = 0; i + parts.length <= segs.length; i += 1) {
        if (parts.every((part, j) => segs[i + j].toLowerCase() === part)) {
          holders.forEach((holder, j) => {
            const at = i + parts.length + j;
            if (at < segs.length && segs[at] !== '' && segs[at].charAt(0) !== '[') segs[at] = holder;
          });
        }
      }
    });
    return segs
      .map((seg) => (seg !== '' && seg.charAt(0) !== '[' ? maskValue(seg) || seg : seg))
      .join('/');
  };

  const clean = (url, depth) => {
    // The fragment never goes out: /mcp/upload keeps its one-time token there.
    const hashAt = url.indexOf('#');
    const noHash = hashAt === -1 ? url : url.slice(0, hashAt);
    const qAt = noHash.indexOf('?');
    let head = qAt === -1 ? noHash : noHash.slice(0, qAt);
    const query = qAt === -1 ? '' : noHash.slice(qAt + 1);

    let origin = '';
    const m = /^([a-z][a-z0-9+.-]*:\/\/[^/]*)(.*)$/i.exec(head);
    if (m) {
      // Drop credentials in the authority (user:pass@host).
      origin = m[1].replace(/\/\/[^/@]*@/, '//');
      [, , head] = m;
    }
    const out = origin + cleanPath(head);
    if (!query) return out;

    const kept = [];
    query.split('&').forEach((pair) => {
      if (pair === '') return;
      const eq = pair.indexOf('=');
      const rawKey = eq === -1 ? pair : pair.slice(0, eq);
      const rawVal = eq === -1 ? '' : pair.slice(eq + 1);
      const key = safeDecode(rawKey);
      if (drop.indexOf(key.toLowerCase()) !== -1) return;
      if (rawVal === '') {
        kept.push(pair);
      } else if (SECRET_PARAM.test(key)) {
        kept.push(`${rawKey}=[redacted]`);
      } else if (NESTED_PARAM.test(key) && depth < 3) {
        // Older links encoded `from` twice: decode once more if still encoded.
        let inner = safeDecode(rawVal);
        if (/%[0-9a-f]{2}/i.test(inner) && /^(\/|%2f|[a-z]+%3a)/i.test(inner)) inner = safeDecode(inner);
        kept.push(`${rawKey}=${encodeURIComponent(clean(inner, depth + 1))}`);
      } else {
        kept.push(`${rawKey}=${maskValue(rawVal) || rawVal}`);
      }
    });
    return kept.length ? `${out}?${kept.join('&')}` : out;
  };

  return clean(input, 0);
}

// Query keys whose values scrubText() hides inside free text (log messages,
// breadcrumbs, exception values). Kept narrower than SECRET_PARAM above: free
// text is not known to be a URL, so short generic keys such as `e` stay out.
const TEXT_SECRET_KEYS = [
  'token|access_token|refresh_token|id_token|code|code_verifier|key|api_key|apikey|secret',
  '|client_secret|password|email|user|uid|id|state|nonce|session_id|signature|otp|t|restore|batchKey',
].join('');
const TEXT_PARAM_RE = new RegExp(`([?&](?:${TEXT_SECRET_KEYS})=)[^&#\\s"'<>)]*`, 'gi');
// The same keys inside an encoded return path: ?from=%2Foauth%2Fconsent%3Fuid%3D…
const TEXT_ENCODED_PARAM_RE = new RegExp(`((?:%3F|%26)(?:${TEXT_SECRET_KEYS})%3D)(?:(?!%26)[^&#\\s"'<>)])*`, 'gi');
const EMAIL_RE = /[A-Za-z0-9._%+-]+(?:@|%40)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g;
const TEXT_PATH_RULES = [
  ['/auth-confirm', 2],
  ['/auth-account-created', 1],
  ['/auth-forgot-sent', 1],
  ['/auth-re-password', 2],
  ['/marketing/unsubscribe', 1],
  ['/unsubscribe', 2],
  ['/email/confirm', 1],
  ['/mcp-uploads', 1],
  ['/oauth/interaction', 1],
].map(([prefix, count]) => ({
  re: new RegExp(`${prefix.replace(/[-/]/g, '\\$&')}((?:/[^/?#\\s"'<>)]*){1,${count}})`, 'g'),
  prefix,
}));
const UPLOAD_HASH_RE = /(\/mcp\/upload)#[^\s"'<>)]*/g;

/**
 * Scrub free text that may contain a URL or an address: page titles, log
 * messages, exception values, breadcrumb messages. Emails become [email];
 * the sensitive paths and query values listed above are masked in place.
 * Unlike sanitizeAnalyticsUrl it has no catch-all for hex strings — free text
 * legitimately carries event ids and file hashes.
 */
export const scrubText = (text) => {
  if (typeof text !== 'string' || text === '') return text;
  let out = text.replace(EMAIL_RE, '[email]');
  TEXT_PATH_RULES.forEach(({ re, prefix }) => {
    out = out.replace(re, (match, rest) => (
      prefix + sanitizeAnalyticsUrl(prefix + rest).slice(prefix.length)
    ));
  });
  return out
    .replace(UPLOAD_HASH_RE, '$1')
    .replace(TEXT_PARAM_RE, '$1[redacted]')
    .replace(TEXT_ENCODED_PARAM_RE, '$1[redacted]');
};

/**
 * Clean the string values of an analytics event's parameters. Event params are
 * meant to be enums and counts, but a free-text error message can carry an
 * address or a link; this is the last line of defence, not a licence.
 * Undefined values are dropped.
 */
export const scrubEventParams = (params) => {
  if (!params || typeof params !== 'object') return params;
  const out = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    // Optional params are passed as undefined; leave them out of the hit.
    if (value === undefined) return;
    out[key] = typeof value === 'string' ? scrubText(value) : value;
  });
  return out;
};

// Keys whose value is a URL and gets the full sanitiser (with the catch-all).
const URL_KEYS = new Set(['url', 'from', 'to', 'Referer', 'referer', 'referrer']);
// Identifiers and binary-ish data in a Sentry envelope that must stay intact.
const SKIP_KEYS = new Set(['event_id', 'trace_id', 'span_id', 'parent_span_id', 'debug_meta', 'sdk', 'fingerprint']);
const MAX_DEPTH = 12;

const walk = (node, depth, seen) => {
  if (!node || typeof node !== 'object' || depth > MAX_DEPTH || seen.has(node)) return;
  seen.add(node);
  Object.keys(node).forEach((key) => {
    if (SKIP_KEYS.has(key)) return;
    const value = node[key];
    if (typeof value === 'string') {
      // Only known URL fields get the catch-all: stack frame paths and messages
      // carry bundle hashes that must stay readable for source maps.
      const isUrl = URL_KEYS.has(key);
      // Mutating in place is the documented Sentry beforeSend pattern.
      // eslint-disable-next-line no-param-reassign
      node[key] = isUrl ? scrubText(sanitizeAnalyticsUrl(value)) : scrubText(value);
    } else if (value && typeof value === 'object') {
      walk(value, depth + 1, seen);
    }
  });
};

/**
 * Scrub a Sentry/GlitchTip event, transaction or breadcrumb in place and
 * return it: request URL, query string and Referer, breadcrumb URLs
 * (navigation from/to, fetch/xhr url), transaction names, messages and
 * exception values.
 */
export const scrubSentryEvent = (event) => {
  if (!event || typeof event !== 'object') return event;
  const { request } = event;
  if (request && typeof request.query_string === 'string' && request.query_string) {
    request.query_string = sanitizeAnalyticsUrl(`?${request.query_string}`).replace(/^\?/, '');
  }
  if (request && request.cookies) delete request.cookies;
  walk(event, 0, new WeakSet());
  return event;
};
